import { ExternalLink } from "lucide-react";
import PageHero from "@/components/PageHero";
import CtaBand from "@/components/CtaBand";
import PortfolioGroups from "@/components/PortfolioGroups";
import { IMAGES, FACILITY_PORTFOLIOS } from "@/lib/data";

export default function Locations() {
  return (
    <div data-testid="locations-page">
      <PageHero
        eyebrow="Locations"
        title="Our Texas Footprint"
        subtitle="Part of the largest emergency services networks in Texas with 27 freestanding emergency rooms and micro-hospitals."
      />

      {/* Intro copy + Texas map */}
      <section data-testid="locations-intro" className="zt-section">
        <div className="zt-container grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <p className="text-base md:text-lg text-slate-600 leading-relaxed">
              ZT Health operates a network of Freestanding ERs, Microhospitals and Physician Staffing services, supporting more than 150,000 patients each year.
            </p>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed mt-4">
              Every facility is designed to bring high-quality emergency, hospital-level, and specialty care to the communities that need it most.
            </p>
          </div>
          <div className="lg:col-span-6">
            <img
              src={IMAGES.altusTexasMap}
              alt="ZT Health facilities across Texas"
              className="w-full h-auto"
              data-testid="locations-texas-map"
            />
          </div>
        </div>
      </section>

      {/* Investment portfolios narrative */}
      <section
        data-testid="locations-portfolio-brands"
        className="zt-section border-t border-slate-200"
        style={{ background: "var(--zt-bg-alt)" }}
      >
        <div className="zt-container">
          <div className="max-w-3xl mb-10">
            <p className="zt-overline mb-4">Our Investment Portfolios</p>
            <h2 className="text-2xl md:text-4xl font-bold tracking-tight leading-tight mb-5">
              Three healthcare portfolios powering the ZT Health platform
            </h2>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed">
              Altus Community Healthcare, Horizon Health Partners, and Vista Health Partners are the investment portfolios behind our facility network. Our clients can have confidence knowing that when they invest with our healthcare portfolios, they will benefit from an industry poised for growth.
            </p>
          </div>
          <div className="bg-white border border-slate-200 p-6 md:p-10">
            <PortfolioGroups linkable={true} layout="landscape" />
          </div>
        </div>
      </section>

      {/* Facility locations, grouped by portfolio → brand */}
      <section data-testid="locations-list" className="zt-section">
        <div className="zt-container">
          <p className="zt-overline mb-4">Our Facilities</p>

          {FACILITY_PORTFOLIOS.map((port) => (
            <div
              key={port.portfolio}
              data-testid={`portfolio-block-${port.portfolio.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="mt-10 first:mt-0"
            >
              <h2
                className="text-xl md:text-2xl font-bold tracking-tight leading-tight mb-6 pb-3 border-b-2"
                style={{ fontFamily: "Outfit, sans-serif", color: "var(--zt-navy)", borderColor: "var(--zt-teal)" }}
              >
                {port.portfolio}
              </h2>

              {port.brands.map((brand) => (
                <div
                  key={`${port.portfolio}-${brand.name}`}
                  data-testid={`brand-${brand.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="mb-10 last:mb-0"
                >
                  <div className="mb-5">
                    <h3
                      className="text-base md:text-lg font-bold uppercase tracking-[0.14em]"
                      style={{ color: "var(--zt-navy)" }}
                    >
                      {brand.name}
                    </h3>
                    {brand.url && (
                      <a
                        href={brand.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`brand-link-${brand.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold uppercase tracking-wider hover:opacity-80 transition-opacity"
                        style={{ color: "var(--zt-teal)" }}
                      >
                        Visit Site <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                    {brand.facilities.map((f) => (
                      <div
                        key={`${brand.name}-${f.name}`}
                        data-testid={`facility-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="border-l-2 pl-5"
                        style={{ borderColor: "var(--zt-teal)" }}
                      >
                        <p className="text-sm font-bold uppercase tracking-wider mb-1" style={{ color: "var(--zt-navy)" }}>
                          {f.name}
                        </p>
                        <address className="not-italic text-sm text-slate-600 leading-relaxed">
                          {f.address1}<br />
                          {f.address2}
                        </address>
                        {f.phone && (
                          <a
                            href={`tel:${f.phone.replace(/[^0-9+]/g, "")}`}
                            className="inline-block mt-2 text-sm font-semibold hover:underline"
                            style={{ color: "var(--zt-teal)" }}
                          >
                            {f.phone}
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
